import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { PixiSettings } from "../../../pixiSettings";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";

// Background scene Y offset config (multiplied by symbolHeight, added to center)
// Positive = push scene DOWN, Negative = push scene UP
export const BACKGROUND_OFFSET_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: -1.6,
    [PixiSettings.ORIENTATION.LANDSCAPE]: -0.5,
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: -1.0,
    [PixiSettings.ORIENTATION.LANDSCAPE]: -0.5,
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: -0.6,
    [PixiSettings.ORIENTATION.LANDSCAPE]: -0.5,
  },
  [PixiSettings.DEVICE.DESKTOP]: -0.2,
};

// Manages background spine animations
class BackgroundAnimationController {
  constructor(
    container,
    backgroundSprites,
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
    slotRegistry,
  ) {
    this.container = container;
    this.backgroundSprites = backgroundSprites;
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolScale = symbolScale;
    this.device = device;
    this.orientation = orientation;
    this.backgroundSpine = null;
    // SlotRegistry functions for centralized injection
    this._registerSlot = slotRegistry?.registerSlot ?? null;
    this._unregisterSlot = slotRegistry?.unregisterSlot ?? null;
    // Slot containers for reel injection
    this.baseReelSlotContainer = null;
    this.featureReelSlotContainer = null;
    // Logo slot containers for bone injection (keyed by slot name)
    this.logoSlotContainers = {};
    // Track current feature state for logo visibility toggling on resize
    this._logoFeatureActive = false;
    // Reference to character controller for coordinated hide/show during transitions
    this.characterController = null;
    // Cache for available animations
    this._availableAnimationsCache = null;
    // Guard flag: prevents React effects from overwriting warmup animations
    this._isWarmingUp = false;
    this.initializeBackground();
  }

  // Initialize background spine animation
  initializeBackground() {
    this.backgroundSpine = this.createBackgroundSpine();

    if (!this.backgroundSpine) {
      return;
    }

    // Simple validation
    if (!this.backgroundSpine.skeleton || !this.backgroundSpine.state) {
      return;
    }

    // Invalidate animations cache when spine is recreated
    this._availableAnimationsCache = null;

    // Setup spine - MUST be behind background images
    this.backgroundSpine.position.set(0, 0);
    this.backgroundSpine.zIndex = 1; // Above background quadrant images (which are zIndex 0)
    this.backgroundSpine.visible = true;

    // Add to container
    this.container.addChild(this.backgroundSpine);

    // Apply responsive scaling and positioning
    this.applyResponsiveBackgroundScaling();

    // Attach slot containers for reel injection (base_reel and feature_reel bones)
    this.attachSlotContainers();

    // Attach logo spine instances to logo bones
    this.attachLogos();

    // Pre-upload all 17 atlas page textures to GPU.
    // Without this, feature_transition's first play causes RAF warnings
    // because base_idle only references ~4 of the 17 atlas pages.
    // Deferred to next frame because globalThis.__PIXI_RENDERER__ is set
    // by the parent Stage useEffect which fires AFTER child effects.
    requestAnimationFrame(() => {
      SpineAtlasUploader.upload("background_animation");

      // Also warm up the feature_transition mesh pipeline.
      // Atlas upload only handles TEXTURE data (gl.texImage2D).
      // Mesh vertex/index buffers are created lazily on first RENDER.
      // feature_transition has ~200 slots with mesh attachments —
      // rendering them once at alpha 0.001 creates all GPU buffers
      // before the user ever triggers the feature.
      this.warmUpTransition();
    });
  }

  // Create background spine instance
  createBackgroundSpine() {
    try {
      // Check if assets are loaded
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "background_animation-skeleton";
      const atlasAlias = "background_animation-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      // Create independent spine instance
      const backgroundSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      // Verify the spine was created properly
      if (
        !backgroundSpine ||
        !backgroundSpine.skeleton ||
        !backgroundSpine.state
      ) {
        return null;
      }

      // Verify skeleton has required data
      if (
        !backgroundSpine.skeleton.data ||
        !backgroundSpine.skeleton.data.animations
      ) {
        return null;
      }

      return backgroundSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Warm up the feature_transition animation by rendering it at near-invisible
   * alpha across multiple seek points. This forces the PixiJS render pipeline
   * to create all mesh vertex/index buffers in WebGL BEFORE the user triggers
   * the feature — eliminating first-play jitter.
   *
   * The atlas uploader only handles TEXTURE uploads (gl.texImage2D).
   * Mesh geometry buffers (gl.createBuffer + gl.bufferData) are created
   * lazily on first render. feature_transition has ~200 mesh attachments
   * that appear at different points in the animation, so we seek to
   * multiple time positions and render a frame at each.
   *
   * Also warms up feature_idle since it plays right after the transition.
   */
  warmUpTransition() {
    if (!this.backgroundSpine?.state) return;

    const animations = this.getAvailableAnimations();
    if (!animations.includes("feature_transition")) return;

    try {
      this._isWarmingUp = true;
      const savedAlpha = this.backgroundSpine.alpha;
      this.backgroundSpine.alpha = 0.001;

      const entry = this.backgroundSpine.state.setAnimation(
        0, "feature_transition", false
      );
      const duration = entry.animation.duration;

      // Seek points across the animation to hit all mesh attachments:
      // - 0.15: early (base-side elements)
      // - 0.50: midpoint (character body parts, rocks, effects all visible)
      // - 0.85: near-end (feature-side elements, close to feature_idle pose)
      const seekPoints = [0.15, 0.50, 0.85];
      let step = 0;

      const advanceAndRender = () => {
        if (!this.backgroundSpine?.state) return;

        if (step < seekPoints.length) {
          // Seek to next time point and let PixiJS render this frame
          const track = this.backgroundSpine.state.tracks[0];
          if (track) {
            track.trackTime = duration * seekPoints[step];
            this.backgroundSpine.state.update(0);
          }
          step++;
          requestAnimationFrame(advanceAndRender);
        } else {
          // All seek points rendered — warm up feature_idle for one frame too
          if (animations.includes("feature_idle")) {
            this.backgroundSpine.state.setAnimation(0, "feature_idle", false);
            this.backgroundSpine.state.update(0.016);

            requestAnimationFrame(() => {
              // Restore to base_idle
              if (this.backgroundSpine?.state) {
                this.backgroundSpine.alpha = savedAlpha;
                this.backgroundSpine.state.setAnimation(0, "base_idle", true);
              }
              this._isWarmingUp = false;
            });
          } else {
            // No feature_idle — just restore
            this.backgroundSpine.alpha = savedAlpha;
            this.backgroundSpine.state.setAnimation(0, "base_idle", true);
            this._isWarmingUp = false;
          }
        }
      };

      // Start the warmup sequence on next frame
      requestAnimationFrame(advanceAndRender);
    } catch (error) {
      // Restore on error
      if (this.backgroundSpine?.state) {
        this.backgroundSpine.alpha = 1;
        this.backgroundSpine.state.setAnimation(0, "base_idle", true);
      }
      this._isWarmingUp = false;
    }
  }

  // Play base_idle animation (for base game)
  playBaseIdle() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }
    // Don't overwrite warmup animation — warmup will restore base_idle when done
    if (this._isWarmingUp) return;

    try {
      // Fade out tension shake sound if it's playing (only if tension is enabled)
      if (FEATURES_CONFIG.TENSION.ENABLED) {
        soundManager.fadeOut(SOUND_NAME.tension_shake, 500);
      }

      // Show spine for base game
      this.backgroundSpine.visible = true;
      // Only clear track 0 (idle animation), leave track 1 (tension) intact
      this.backgroundSpine.state.clearTrack(0);
      this.backgroundSpine.state.setAnimation(0, "base_idle", true);
    } catch (error) {
      // Fail silently
    }
  }

  // Play feature_idle animation (for Hold and Win feature)
  playFeatureIdle() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }
    // Don't overwrite warmup animation
    if (this._isWarmingUp) return;

    try {
      // Fade out tension shake sound if it's playing (only if tension is enabled)
      if (FEATURES_CONFIG.TENSION.ENABLED) {
        soundManager.fadeOut(SOUND_NAME.tension_shake, 500);
      }

      // Keep spine visible - it should always be visible
      // Only clear track 0 (idle animation), leave track 1 (tension) intact
      this.backgroundSpine.state.clearTrack(0);
      this.backgroundSpine.state.setAnimation(0, "feature_idle", true);
    } catch (error) {
      // Silent fail
    }
  }

  // Play base_event intro animation on track 1 (tension start)
  // Track 0 continues playing base_idle/feature_idle without interruption
  playBaseEventIn() {
    // Skip if tension is disabled
    if (!FEATURES_CONFIG.TENSION.ENABLED) {
      return;
    }

    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    try {
      // Start looping tension shake sound
      soundManager.play(SOUND_NAME.tension_shake);

      const animations = this.getAvailableAnimations();

      // Play tension animation on track 1 ONLY (track 0 stays on whatever idle is already playing)
      // The React component handles switching between base_idle/feature_idle based on holdAndWinActive
      if (
        animations.includes("base_event_in") &&
        animations.includes("base_event_idle")
      ) {
        this.backgroundSpine.state.setAnimation(1, "base_event_in", false);
        this.backgroundSpine.state.addAnimation(1, "base_event_idle", true, 0);
      } else if (animations.includes("base_event_idle")) {
        // Fallback: just play loop if intro not available
        this.backgroundSpine.state.setAnimation(1, "base_event_idle", true);
      }
    } catch (error) {
      // Fail silently
    }
  }

  // Play base_event exit animation on track 1 (tension end)
  // Track 0 continues playing base_idle/feature_idle without interruption
  playBaseEventOut() {
    // Skip if tension is disabled
    if (!FEATURES_CONFIG.TENSION.ENABLED) {
      return;
    }

    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    try {
      // Fade out tension shake sound
      soundManager.fadeOut(SOUND_NAME.tension_shake, 500);

      const currentTrack = this.backgroundSpine.state.tracks[1];
      const currentAnimation = currentTrack?.animation?.name;

      // Check if we're currently playing base_event animations on track 1
      const isPlayingBaseEvent =
        currentAnimation === "base_event_in" ||
        currentAnimation === "base_event_idle";

      if (!isPlayingBaseEvent) {
        return;
      }

      const animations = this.getAvailableAnimations();

      // Play exit animation on track 1 if available
      if (animations.includes("base_event_out")) {
        this.backgroundSpine.state.setAnimation(1, "base_event_out", false);
        // Track 1 will be empty after exit animation completes (track 0 continues idle)
      } else {
        // Fallback: just clear track 1 if exit animation not available
        this.backgroundSpine.state.clearTrack(1);
      }
    } catch (error) {
      // Fail silently
    }
  }

  // Play feature_transition animation on track 0 (one-shot for testing/triggering)
  // After it completes, returns to base_idle looping
  // Hides the character during transition (the transition animation contains the character)
  playFeatureTransition() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      // Wait for character idle to finish its current loop, then start transition
      const startTransition = () => {
        try {
          const animations = this.getAvailableAnimations();
          if (animations.includes("feature_transition")) {
            const entry = this.backgroundSpine.state.setAnimation(
              0,
              "feature_transition",
              false,
            );
            // Queue feature_idle to loop after transition completes
            if (animations.includes("feature_idle")) {
              this.backgroundSpine.state.addAnimation(
                0,
                "feature_idle",
                true,
                0,
              );
            }

            // Resolve when transition animation completes
            const listener = {
              complete: (trackEntry) => {
                if (trackEntry === entry) {
                  this.backgroundSpine.state.removeListener(listener);

                  // Clean up transition-only slots: feature_transition keys ~200
                  // slots (character body parts, rocks, effects) that feature_idle
                  // does NOT key. Without cleanup, those attachments persist as a
                  // static image. Same pattern as stopFrontMask().
                  this.cleanupTransitionSlots();

                  // Show character immediately — this runs synchronously during
                  // the spine ticker, so the animation is applied before the
                  // next render frame (no stale-pose artifact).
                  if (this.characterController) {
                    this.characterController.show();
                  }
                  resolve();
                }
              },
            };
            this.backgroundSpine.state.addListener(listener);
          } else {
            resolve();
          }
        } catch (error) {
          resolve();
        }
      };

      // Let character finish its current idle cycle, then hide and fire transition
      if (this.characterController) {
        this.characterController.finishIdleAndHide(startTransition);
      } else {
        startTransition();
      }
    });
  }

  // Play front_mask animation on track 2 (flowers/tree overlay for intro popup)
  // Track 0 (idle) and track 1 (tension) continue unaffected
  playFrontMask() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    try {
      const animations = this.getAvailableAnimations();
      if (animations.includes("front_mask")) {
        this.backgroundSpine.state.setAnimation(2, "front_mask", true);
      }
    } catch (error) {
      // Fail silently
    }
  }

  // Stop front_mask animation and hide all its slots
  stopFrontMask() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    try {
      this.backgroundSpine.state.clearTrack(2);

      // Null out all slot attachments used by front_mask so they become invisible
      const frontMaskSlots = [
        "bg_flower_01",
        "bg_flower_02",
        "bg_flower_03",
        "bg_flower_04",
        "bg_flower_07",
        "bg_flower_08",
        "tree_mask",
      ];
      const skeleton = this.backgroundSpine.skeleton;
      frontMaskSlots.forEach((slotName) => {
        const slot = skeleton.findSlot(slotName);
        if (slot) {
          slot.setAttachment(null);
        }
      });
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Clean up slots that feature_transition keys but feature_idle does NOT.
   * Without this, ~200 slot attachments (character body parts, rocks, drill,
   * effects) persist as static images when feature_idle takes over.
   *
   * Dynamically computes the keyed slots from feature_idle's skeleton data
   * and nulls everything else — future-proof if animations change.
   */
  cleanupTransitionSlots() {
    if (!this.backgroundSpine?.skeleton?.data) return;

    try {
      const skeleton = this.backgroundSpine.skeleton;
      const skeletonData = skeleton.data;

      // Get the set of slot names that feature_idle keys
      const featureIdleAnim = skeletonData.findAnimation("feature_idle");
      const featureIdleSlots = new Set();
      if (featureIdleAnim?.timelines) {
        for (const timeline of featureIdleAnim.timelines) {
          if (timeline.slotIndex !== undefined) {
            const slot = skeleton.slots[timeline.slotIndex];
            if (slot) featureIdleSlots.add(slot.data.name);
          }
        }
      }

      // Null out attachments in all slots NOT keyed by feature_idle.
      // The setup pose has most character slots with null attachment,
      // so this only affects slots that feature_transition actively set.
      for (const slot of skeleton.slots) {
        if (!featureIdleSlots.has(slot.data.name) && slot.attachment) {
          slot.setAttachment(null);
        }
      }
    } catch (error) {
      // Fail silently
    }
  }

  // Apply responsive scaling and positioning to maintain stone ledge alignment
  applyResponsiveBackgroundScaling() {
    if (!this.dimensions) return;

    const { width, height } = this.dimensions;

    // Only scale background when symbols start shrinking (scaleFactor < 1)
    const backgroundScale = Math.min(1, this.symbolScale);

    const centerX = width / 2;

    // Follow background offset config (positions entire scene per device/orientation)
    const deviceConfig = BACKGROUND_OFFSET_CONFIG[this.device];
    const offsetMultiplier =
      typeof deviceConfig === "number"
        ? deviceConfig
        : (deviceConfig?.[this.orientation] ?? 0);
    const backgroundOffset = this.symbolHeight * offsetMultiplier;
    const positionY = height / 2 + backgroundOffset;

    // Apply unified scaling and positioning to all 4 quadrant sprites
    if (this.backgroundSprites) {
      const sprites = [
        this.backgroundSprites.topLeft,
        this.backgroundSprites.topRight,
        this.backgroundSprites.bottomLeft,
        this.backgroundSprites.bottomRight,
      ];

      sprites.forEach((spriteRef) => {
        const sprite = spriteRef?.current || spriteRef;
        if (sprite && sprite.scale && sprite.position) {
          sprite.scale.set(backgroundScale);
          sprite.position.set(centerX, positionY);
        }
      });
    }

    // Spine uses same scale and position — they always move together
    if (this.backgroundSpine) {
      this.backgroundSpine.scale.set(backgroundScale);
      this.backgroundSpine.position.set(centerX, positionY);
    }
  }

  // Update dimensions and reapply scaling
  resizeBackground(
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
  ) {
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolScale = symbolScale;
    this.device = device;
    this.orientation = orientation;
    this.applyResponsiveBackgroundScaling();

    // Re-evaluate logo visibility when orientation changes
    if (Object.keys(this.logoSlotContainers).length > 0) {
      this.setLogoMode(this._logoFeatureActive, orientation);
    }
  }

  // Get list of available animation names
  // Result is cached to avoid repeated array mapping
  getAvailableAnimations() {
    // Return cached result if available
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    if (!this.backgroundSpine?.skeleton?.data?.animations) {
      this._availableAnimationsCache = [];
      return [];
    }

    this._availableAnimationsCache =
      this.backgroundSpine.skeleton.data.animations.map((anim) => anim.name);
    return this._availableAnimationsCache;
  }

  // Attach empty containers to base_reel and feature_reel slots for reel injection
  // External display objects (ReelBack, ReelSet, ReelFront) can be added as children
  // and will follow the bone's transform during animations (e.g. feature_transition)
  attachSlotContainers() {
    if (!this.backgroundSpine || !this.backgroundSpine.skeleton) return;

    // Attach to base_reel slot (base game reel position)
    const baseReelSlot = this.backgroundSpine.skeleton.findSlot("base_reel");
    if (baseReelSlot) {
      this.baseReelSlotContainer = new PIXI.Container();
      this.baseReelSlotContainer.name = "base_reel_slot_content";
      this.backgroundSpine.addSlotObject(
        "base_reel",
        this.baseReelSlotContainer,
      );
      // Register with SlotRegistry for centralized access (gated by per-component config)
      if (this._registerSlot && FEATURES_CONFIG.SLOT_REGISTRY.REELSET_INJECTION) {
        this._registerSlot("background.base_reel", this.baseReelSlotContainer);
      }
    }

    // Attach to feature_reel slot (feature game reel position)
    const featureReelSlot =
      this.backgroundSpine.skeleton.findSlot("feature_reel");
    if (featureReelSlot) {
      this.featureReelSlotContainer = new PIXI.Container();
      this.featureReelSlotContainer.name = "feature_reel_slot_content";
      this.backgroundSpine.addSlotObject(
        "feature_reel",
        this.featureReelSlotContainer,
      );
      // Register with SlotRegistry for centralized access (gated by per-component config)
      if (this._registerSlot && FEATURES_CONFIG.SLOT_REGISTRY.REELSET_INJECTION) {
        this._registerSlot("background.feature_reel", this.featureReelSlotContainer);
      }
    }

  }

  // ============================================
  // LOGO SLOT ATTACHMENT
  // ============================================

  // Logo slot names in the background spine (each maps to a bone)
  static LOGO_SLOTS = [
    "logo_base_landscape_position",
    "logo_base_portrait_position",
    "logo_feature_landscape_position",
    "logo_feature_portrait_position",
  ];

  // Attach empty containers to logo bone slots for external content injection.
  // Same pattern as base_reel/feature_reel — Logo components inject their
  // own spine/sprite content and handle their own scaling.
  // The bone provides positioning only.
  attachLogos() {
    if (!this.backgroundSpine || !this.backgroundSpine.skeleton) return;

    for (const slotName of BackgroundAnimationController.LOGO_SLOTS) {
      const slot = this.backgroundSpine.skeleton.findSlot(slotName);
      if (!slot) continue;

      const isLandscape = slotName.includes("landscape");

      const slotContainer = new PIXI.Container();
      slotContainer.name = `${slotName}_content`;

      // Initial alpha: show logos matching current orientation
      const currentIsLandscape =
        this.device === PixiSettings.DEVICE.DESKTOP ||
        this.orientation !== PixiSettings.ORIENTATION.PORTRAIT;
      slotContainer.alpha = isLandscape === currentIsLandscape ? 1 : 0.001;

      this.backgroundSpine.addSlotObject(slotName, slotContainer);
      this.logoSlotContainers[slotName] = {
        container: slotContainer,
        isLandscape,
      };

      // Register with SlotRegistry for centralized access (gated by per-component config)
      // Map spine slot name → registry slot name (e.g. "logo_base_landscape_position" → "background.logo_base_landscape")
      if (this._registerSlot && FEATURES_CONFIG.SLOT_REGISTRY.LOGO_INJECTION) {
        const registryName = "background." + slotName.replace("_position", "");
        this._registerSlot(registryName, slotContainer);
      }
    }
  }

  // Get the slot container for a specific logo bone.
  // Used by LogoLandscape/LogoPortrait to inject their content.
  getLogoSlotContainer(slotName) {
    return this.logoSlotContainers?.[slotName]?.container ?? null;
  }

  // Toggle logo alpha based on game state and orientation.
  // All 4 logo slot containers are always rendered — alpha controls which is shown.
  // Desktop is treated as landscape.
  setLogoMode(isFeatureActive, orientation) {
    this._logoFeatureActive = isFeatureActive;
    if (!this.logoSlotContainers) return;

    const resolvedOrientation = orientation || this.orientation;
    const isLandscape =
      this.device === PixiSettings.DEVICE.DESKTOP ||
      resolvedOrientation !== PixiSettings.ORIENTATION.PORTRAIT;

    for (const [slotName, entry] of Object.entries(this.logoSlotContainers)) {
      const isBase = slotName.includes("base");
      const showOrientation = entry.isLandscape === isLandscape;
      const showState = isBase !== isFeatureActive; // base shown when !feature, feature shown when feature
      entry.container.alpha = showOrientation && showState ? 1 : 0.001;
    }
  }
}

export default BackgroundAnimationController;
